import React, { useRef } from 'react';
import { Button, Box, Chip } from '@mui/material';
import { Upload as UploadIcon, Close as CloseIcon } from '@mui/icons-material';
import { toastService } from './Toast';

export interface FileUploadProps {
  label: string;
  accept?: string; // e.g., "image/*,video/*"
  multiple?: boolean;
  maxFiles?: number;
  files: File[];
  onFilesChange: (files: File[]) => void;
  disabled?: boolean;
  variant?: 'positive' | 'negative' | 'neutral' | 'purple';
}

export const FileUpload: React.FC<FileUploadProps> = ({
  label,
  accept,
  multiple = false,
  maxFiles,
  files,
  onFilesChange,
  disabled = false,
  variant = 'neutral',
}) => {
  const inputRef = useRef<HTMLInputElement>(null);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFiles = Array.from(e.target.files || []);
    
    if (maxFiles && selectedFiles.length > maxFiles) {
      toastService.warning(`Maximum ${maxFiles} file${maxFiles > 1 ? 's' : ''} allowed`);
      return;
    }

    onFilesChange(selectedFiles);
  };

  const handleRemoveFile = (index: number) => {
    const newFiles = files.filter((_, i) => i !== index);
    onFilesChange(newFiles);
  };

  const handleClick = () => {
    inputRef.current?.click();
  };

  return (
    <Box className={`file-upload-container file-upload-${variant}`}>
      <input
        ref={inputRef}
        type="file"
        accept={accept}
        multiple={multiple}
        onChange={handleFileChange}
        disabled={disabled}
        style={{ display: 'none' }}
      />
      
      <Button
        variant="outlined"
        onClick={handleClick}
        disabled={disabled}
        startIcon={<UploadIcon />}
        className={`file-upload-button file-upload-button-${variant}`}
        fullWidth
      >
        {label}
      </Button>

      {files.length > 0 && (
        <Box className="file-upload-chips">
          {files.map((file, index) => (
            <Chip
              key={`${file.name}-${index}`}
              label={file.name}
              onDelete={() => handleRemoveFile(index)}
              deleteIcon={<CloseIcon />}
              size="small"
              className={`file-chip file-chip-${variant}`}
            />
          ))}
        </Box>
      )}
    </Box>
  );
};
